import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import baseUrl from "../api/utils";

const fmt = (n) => (n || n === 0) ? Number(n).toLocaleString("en-IN") : "0";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const daysLeft = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  expiry.setHours(0, 0, 0, 0);
  return Math.round((expiry - today) / (1000 * 60 * 60 * 24));
};

// ═══════════════════════════════════════════════════════════════════════════════
// SYSADMIN DASHBOARD — same inv-kpi-strip / inv-card / inv-table classes
// used by the ADMIN dashboard (Dashboard.jsx), pulling from
// /api/v2/license/dashboard-summary instead of company-level sales data.
// ═══════════════════════════════════════════════════════════════════════════════
const SysAdminDashboard = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("token");

  const [summary, setSummary] = useState({
    totalCompanies: 0,
    saasCompanies: 0,
    vpsInstallations: 0,
    totalLicenseUsers: 0,
    recentlyRegistered: [],
    expiringSoon: [],
    expiredCount: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${baseUrl}/api/v2/license/dashboard-summary`, {
          headers: { Authorization: token },
        });
        if (res.status === 200) setSummary(res.data);
      } catch (error) {
        if (error.response?.status === 401) navigate("/unauthorized");
        else console.error("Sysadmin dashboard fetch failed:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, []);

  const recentlyRegistered = summary.recentlyRegistered || [];
  const expiringSoon = summary.expiringSoon || [];

  return (
    <>
      {/* Page Header */}
      <div className="page-header">
        <div className="page-block">
          <div className="row align-items-center">
            <div className="col-md-12">
              <div className="page-header-title">
                <h5 className="m-b-10">Super Admin Dashboard</h5>
              </div>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="#" onClick={() => navigate("/sysadmin/dashboard")}>
                    <i className="feather icon-home"></i>
                  </a>
                </li>
                <li className="breadcrumb-item"><a href="#">Dashboard</a></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="inv-dash-wrapper">

        {/* ── TOP KPI STRIP (4 cards) ── */}
        <div
          className="inv-kpi-strip"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "18px",
            marginBottom: "28px",
          }}
        >

          <div className="inv-kpi-card" onClick={() => navigate("/sysadmin/companies")}>
            <div className="inv-kpi-icon yellow">
              <i className="feather icon-briefcase"></i>
            </div>
            <div>
              <div className="inv-kpi-num">{loading ? "…" : fmt(summary.totalCompanies)}</div>
              <div className="inv-kpi-label">Total Companies</div>
            </div>
            <span className="inv-kpi-badge yellow">all</span>
          </div>

          <div className="inv-kpi-card" onClick={() => navigate("/sysadmin/company-licenses")}>
            <div className="inv-kpi-icon green">
              <i className="feather icon-cloud"></i>
            </div>
            <div>
              <div className="inv-kpi-num">{loading ? "…" : fmt(summary.saasCompanies)}</div>
              <div className="inv-kpi-label">SaaS Companies</div>
            </div>
            <span className="inv-kpi-badge green">saas</span>
          </div>

          <div className="inv-kpi-card" onClick={() => navigate("/sysadmin/company-licenses")}>
            <div className="inv-kpi-icon dark">
              <i className="feather icon-server"></i>
            </div>
            <div>
              <div className="inv-kpi-num">{loading ? "…" : fmt(summary.vpsInstallations)}</div>
              <div className="inv-kpi-label">VPS Installations</div>
            </div>
            <span className="inv-kpi-badge dark">vps</span>
          </div>

          <div className="inv-kpi-card inv-kpi-dark">
            <div className="inv-kpi-icon white">
              <i className="feather icon-users"></i>
            </div>
            <div>
              <div className="inv-kpi-num">{loading ? "…" : fmt(summary.totalLicenseUsers)}</div>
              <div className="inv-kpi-label">Total License Users</div>
            </div>
            <span className="inv-kpi-badge blue">capacity</span>
          </div>

        </div>

        {/* ── TABLES ── */}
        <div
          className="inv-table-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "18px",
            marginBottom: "28px",
          }}
        >

          {/* Recently Registered Companies */}
          <div className="inv-card">
            <div className="inv-card-header">
              <div>
                <div className="inv-card-title">Recently Registered Companies</div>
                <div className="inv-card-sub">Latest 5 companies (SaaS + VPS)</div>
              </div>
              <button className="inv-view-btn" onClick={() => navigate("/sysadmin/company-licenses")}>View all</button>
            </div>
            <div className="inv-table-scroll">
              <table className="inv-table">
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Status</th>
                    <th>Valid Till</th>
                  </tr>
                </thead>
                <tbody>
                  {recentlyRegistered.length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: "center", color: "#aaa" }}>No companies yet</td></tr>
                  ) : recentlyRegistered.map((c, i) => (
                    <tr key={i}>
                      <td>
                        <div className="inv-name-cell">
                          <span>{c.companyName || c.company || "—"}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`inv-badge ${c.licenseStatus === "EXPIRED" ? "red" : "green"}`}>
                          {c.licenseStatus || "—"}
                        </span>
                      </td>
                      <td>{formatDate(c.expiryDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Licenses Expiring Soon */}
          <div className="inv-card">
            <div className="inv-card-header">
              <div>
                <div className="inv-card-title">Licenses Expiring Soon</div>
                <div className="inv-card-sub">Within the next 30 days</div>
              </div>
              <button className="inv-view-btn" onClick={() => navigate("/sysadmin/company-licenses")}>View all</button>
            </div>
            <div className="inv-table-scroll">
              <table className="inv-table">
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Valid Till</th>
                    <th>Days Left</th>
                  </tr>
                </thead>
                <tbody>
                  {expiringSoon.length === 0 ? (
                    <tr><td colSpan={3} style={{ textAlign: "center", color: "#aaa" }}>Nothing expiring soon</td></tr>
                  ) : expiringSoon.map((c, i) => {
                    const dLeft = daysLeft(c.expiryDate);
                    return (
                      <tr key={i}>
                        <td>
                          <div className="inv-name-cell">
                            <span>{c.companyName || c.company || "—"}</span>
                          </div>
                        </td>
                        <td>{formatDate(c.expiryDate)}</td>
                        <td><span className="inv-badge red">{dLeft !== null ? `${dLeft}d` : "—"}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </div>

        {/* ── BOTTOM KPI STRIP (2 cards, same sizing as top strip for consistency) ── */}
        <div style={{ fontSize: "13px", fontWeight: 600, color: "#888", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.4px" }}>
          License Health
        </div>
        <div
          className="inv-kpi-strip"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "18px",
          }}
        >

          <div className="inv-kpi-card" onClick={() => navigate("/sysadmin/company-licenses")}>
            <div className="inv-kpi-icon red">
              <i className="feather icon-alert-triangle"></i>
            </div>
            <div>
              <div className="inv-kpi-num">{loading ? "…" : fmt(summary.expiredCount)}</div>
              <div className="inv-kpi-label">Expired Licenses</div>
            </div>
            <span className="inv-kpi-badge red">expired</span>
          </div>

          <div className="inv-kpi-card" onClick={() => navigate("/sysadmin/company-licenses")}>
            <div className="inv-kpi-icon yellow">
              <i className="feather icon-clock"></i>
            </div>
            <div>
              <div className="inv-kpi-num">{loading ? "…" : expiringSoon.length}</div>
              <div className="inv-kpi-label">Expiring (30 days)</div>
            </div>
            <span className="inv-kpi-badge yellow">soon</span>
          </div>

        </div>

      </div>
    </>
  );
};

export default SysAdminDashboard;